import { useEffect, useState } from 'react';
import { useAsync } from 'react-async-hook';
import EmailReportSubscription, {
    AdminEmailReportConfiguration,
    EmailReportType,
} from '../../../../../types/EmailReportSubscription';
import api from '../../../../api';
import { ErrorCard } from '../../../../routing/RouteError';
import { Card, CardHeader, CardHeaderHeading, CardSection } from '../../../components/Cards';
import { Grid, GridItem } from '../../../components/Grid';
import PageHeader from '../../../components/PageHeader';
import PageStaticSection from '../../../components/PageStaticSection';
import PageTitle from '../../../components/PageTitle';
import PillBox from '../../../components/PillBox';
import triggerPopupAlert from '../../../components/PopupAlert/trigger';
import SaveButton, { DISABLED, ENABLED, SAVING } from '../../../components/SaveButton';
import Spinner from '../../../components/Spinner';
import AdminReportSubscriptionForm from './AdminReportSubscriptionForm';
import styles from './style.m.less';

export default function AdminEmailReportsPage() {
    const [adminReportSubscription, setAdminReportSubscription] = useState<EmailReportSubscription | undefined | null>(
        undefined
    );
    const {
        result: adminReport,
        loading,
        error,
    } = useAsync(() => api.emailReportSubscriptions.getReportSubscription('ADMIN_REPORT'), []);

    useEffect(() => {
        setAdminReportSubscription(adminReport);
    }, [adminReport, setAdminReportSubscription]);
    const [unsubscribing, setUnsubscribing] = useState(false);

    const handleSubscribe = (configuration: AdminEmailReportConfiguration) =>
        api.emailReportSubscriptions
            .createReportSubscription({ reportType: 'ADMIN_REPORT', configuration })
            .then(setAdminReportSubscription)
            .catch(() => {
                triggerPopupAlert(
                    "We couldn't subscribe to this report. Please try again or contact support.",
                    'error'
                );
            });

    const handleUnsubscribe = (reportType: EmailReportType) => {
        setUnsubscribing(true);
        api.emailReportSubscriptions.deleteReportSubscription(reportType).then(
            () => {
                setUnsubscribing(false);
                setAdminReportSubscription(undefined);
            },
            () => {
                setUnsubscribing(false);
                triggerPopupAlert(
                    "We couldn't unsubscribe you from this report. Please try again or contact support.",
                    'error'
                );
            }
        );
    };

    const getFrequency = (configuration: AdminEmailReportConfiguration) => {
        switch (configuration.reportFrequencyWeeks) {
            case 1:
                return 'once per week';
            case 2:
                return 'once every two weeks';
            default:
                return `${configuration.reportFrequencyWeeks} weeks`;
        }
    };

    return (
        <>
            <PageTitle title="Receive email reports" />
            <PageHeader title="Receive email reports" />
            <PageStaticSection>
                {error && <ErrorCard errorCode={error.response?.status ?? 500} />}
                {!error && (
                    <Card>
                        <CardHeader>
                            <CardHeaderHeading>
                                Administrative Reporting <PillBox className={styles.beta} color="BLUE" value="ʙᴇᴛᴀ" />
                            </CardHeaderHeading>
                        </CardHeader>
                        <CardSection>
                            <p>
                                From here you can subscribe to receive reports over email, designed to help you
                                administer your performance process.
                            </p>
                            {loading && <Spinner />}
                            {!loading && !error && !adminReportSubscription && (
                                <Grid>
                                    <GridItem desktop="half" s="full">
                                        <AdminReportSubscriptionForm onSubmit={handleSubscribe} />
                                    </GridItem>
                                </Grid>
                            )}
                            {!loading && !error && adminReportSubscription && (
                                <>
                                    You are subscribed to receive this report{' '}
                                    {getFrequency(adminReportSubscription.configuration)}{' '}
                                    <SaveButton
                                        onClick={() => handleUnsubscribe('ADMIN_REPORT')}
                                        state={unsubscribing ? SAVING : ENABLED}
                                        labels={{
                                            [ENABLED]: `Unsubscribe`,
                                            [DISABLED]: `Unsubscribe`,
                                            [SAVING]: `Unsubscribing`,
                                        }}
                                    />
                                </>
                            )}
                        </CardSection>
                    </Card>
                )}
            </PageStaticSection>
        </>
    );
}
